/**
 * mediaCatalog — the Media Library's Phase-1 catalog, DERIVED (no persisted
 * index, no backfill). Two sources, unioned by content address:
 *   • grid scan   — every `image` item's `contentProps.sha256` (this is exactly
 *     what publish's `collectImageAssets` collects) → gives refCount + mime/ext.
 *   • OPFS list   — `listStoredShas` → surfaces ORPHANS (stored locally but no
 *     longer referenced) and confirms which referenced shas are local.
 *
 * So an existing site's panel populates on first open with zero migration. The
 * Yjs asset index (names/history/cross-peer) is a Phase-2 addition on top; see
 * docs/plans/2026-07-22-media-library.md.
 */
import { useEffect, useState } from "react";
import { useGridStore } from "@fredericrous/grid";

import { useSiteId } from "../SiteContext.js";
import { listStoredShas } from "./opfsAssetStore.js";

export type MediaKind = "image" | "video" | "audio";

/** The set of contentTypes that carry a content-addressed asset (sha256). */
function isMediaType(
  ct: string | undefined,
): ct is "image" | "video" | "audio" {
  return ct === "image" || ct === "video" || ct === "audio";
}

/** contentType → catalog kind (unknown → image, matching the orphan default). */
function kindOf(ct: string | undefined): MediaKind {
  return ct === "video" || ct === "audio" ? ct : "image";
}

export interface CatalogAsset {
  readonly sha256: string;
  /** image, video, or audio (from the referencing widget; orphans → image). */
  readonly kind: MediaKind;
  /** From a referencing widget's contentProps, if any (orphans have none). */
  readonly mime?: string;
  readonly ext?: string;
  /** How many image/video items reference this sha across the whole doc. */
  readonly refCount: number;
  /** Present in this site's local OPFS bucket. */
  readonly stored: boolean;
}

// Structural shapes so the scan is testable without grid internals.
interface ScanItem {
  readonly contentType?: string;
  readonly contentProps?: Record<string, unknown> | null;
}
interface ScanGrid {
  readonly items: Record<string, ScanItem>;
}

interface RefInfo {
  mime?: string;
  ext?: string;
  refCount: number;
  kind: MediaKind;
}

/**
 * Referenced content addresses (image AND video) across all grids → mime/ext +
 * reference count + kind. Pure; mirrors `collectImageAssets` so the library,
 * publish, and orphan-detection agree on what "referenced" means — critically,
 * video refs are counted so a video isn't mistaken for an image orphan.
 */
export function scanGridRefs(
  grids: Record<string, ScanGrid>,
): Map<string, RefInfo> {
  const refs = new Map<string, RefInfo>();
  for (const grid of Object.values(grids)) {
    for (const item of Object.values(grid.items)) {
      if (!isMediaType(item.contentType)) continue;
      const cp = item.contentProps ?? {};
      const sha = cp["sha256"];
      if (typeof sha !== "string" || sha.length === 0) continue;
      const kind: MediaKind = kindOf(item.contentType);
      const prev = refs.get(sha);
      if (prev) {
        prev.refCount += 1;
        prev.mime ??= typeof cp["mime"] === "string" ? cp["mime"] : undefined;
        prev.ext ??= typeof cp["ext"] === "string" ? cp["ext"] : undefined;
      } else {
        refs.set(sha, {
          refCount: 1,
          kind,
          mime: typeof cp["mime"] === "string" ? cp["mime"] : undefined,
          ext: typeof cp["ext"] === "string" ? cp["ext"] : undefined,
        });
      }
    }
  }
  return refs;
}

/** Merge grid references with the local OPFS listing into the catalog, sorted
 *  most-referenced first (orphans, refCount 0, last). Pure over its inputs. */
export function mergeCatalog(
  refs: Map<string, RefInfo>,
  storedShas: readonly string[],
): CatalogAsset[] {
  const stored = new Set(storedShas);
  const out: CatalogAsset[] = [];
  for (const sha of new Set([...refs.keys(), ...stored])) {
    const info = refs.get(sha);
    out.push({
      sha256: sha,
      kind: info?.kind ?? "image",
      mime: info?.mime,
      ext: info?.ext,
      refCount: info?.refCount ?? 0,
      stored: stored.has(sha),
    });
  }
  out.sort(
    (a, b) => b.refCount - a.refCount || a.sha256.localeCompare(b.sha256),
  );
  return out;
}

/**
 * Rewrite every image item bound to `oldSha` → `newSha` across all grids — the
 * mechanism behind "replace everywhere". Widgets stay sha-bound, so render /
 * publish / export / scan need no resolver (plan §7 Q3). Returns the count.
 */
export function rewriteImageSha(
  oldSha: string,
  newSha: string,
  meta?: { mime?: string; ext?: string },
): number {
  if (oldSha === newSha) return 0;
  const st = useGridStore.getState();
  let n = 0;
  for (const [gridId, grid] of Object.entries(st.grids)) {
    for (const [itemId, item] of Object.entries(grid.items)) {
      if (!isMediaType(item.contentType)) continue;
      if (item.contentProps?.["sha256"] !== oldSha) continue;
      const next: Record<string, unknown> = {
        ...item.contentProps,
        sha256: newSha,
      };
      if (meta?.mime) next["mime"] = meta.mime;
      if (meta?.ext) next["ext"] = meta.ext;
      st.updateItem(gridId, itemId, { contentProps: next });
      n += 1;
    }
  }
  return n;
}

/** The content-address + type of an asset to bind into a media widget. */
export interface BindAsset {
  readonly sha256: string;
  readonly mime?: string;
  readonly ext?: string;
}

/**
 * Bind an asset into a single media item (the pure store write shared by the
 * Media Library "pick" and drag-to-fill). Sets sha256/mime/ext, drops any legacy
 * `url`. NO UI-mode side effects (no disarm, no toast) — callers own those.
 */
export function bindAssetToMediaItem(
  gridId: string,
  itemId: string,
  asset: BindAsset,
): void {
  const st = useGridStore.getState();
  const cur = st.grids[gridId]?.items[itemId];
  if (!cur) return;
  const next: Record<string, unknown> = { ...(cur.contentProps ?? {}) };
  next["sha256"] = asset.sha256;
  if (asset.mime) next["mime"] = asset.mime;
  if (asset.ext) next["ext"] = asset.ext;
  delete next["url"];
  st.updateItem(gridId, itemId, { contentProps: next });
}

/** Derive the catalog for a site from the live grids + its OPFS bucket. */
export async function deriveCatalog(
  siteId: string | null,
  grids: Record<string, ScanGrid>,
): Promise<CatalogAsset[]> {
  const refs = scanGridRefs(grids);
  const storedShas = siteId ? await listStoredShas(siteId) : [];
  return mergeCatalog(refs, storedShas);
}

/**
 * The catalog, re-derived when the set of referenced shas changes (upload,
 * replace, delete) or on explicit refresh. Keyed on a stable sha-set string so
 * editing text widgets etc. doesn't thrash the OPFS listing.
 */
export function useMediaCatalog(): {
  assets: CatalogAsset[];
  loading: boolean;
  refresh: () => void;
} {
  const siteId = useSiteId();
  const [assets, setAssets] = useState<CatalogAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  // Coarse trigger: the sorted set of referenced shas. Zustand bails the
  // re-render unless this string actually changes.
  const refKey = useGridStore((s) => {
    const shas: string[] = [];
    for (const g of Object.values(s.grids)) {
      for (const it of Object.values(g.items)) {
        const sha = it.contentProps?.["sha256"];
        if (typeof sha === "string") shas.push(sha);
      }
    }
    return shas.sort().join(",");
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void deriveCatalog(siteId, useGridStore.getState().grids).then((a) => {
      if (!cancelled) {
        setAssets(a);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // refKey drives re-derivation on asset-set changes; tick forces refresh.
  }, [siteId, refKey, tick]);

  return { assets, loading, refresh: () => setTick((t) => t + 1) };
}
